import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { AccordionBody, AccordionHeader, AccordionItem, Badge, Button, UncontrolledAccordion } from 'reactstrap'
import auth from '../../api/auth/hooks'
import notes from '../../api/notes/hooks'
import { MODAL_TYPES } from '../../common/constants'
import { selectAccordionPagination, setAccordionPage } from '../../store/accordionPagination'
import { showModal } from '../../store/modal'
import { formatDateTime } from '../../utils/general'
import SkeletonAccordion from '../Common/Skeleton/SkeletonAccordion'
import { setDeleteCallback } from '../Modals/DeleteModal/utils'

const NotesViewAccordion = ({ entityId, noteEntity = '' }) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const page = useSelector((state) => selectAccordionPagination(state, 'notes').currentPage)

    const { NOTES_MODAL, DELETE_MODAL } = MODAL_TYPES

    const notesParams = useMemo(
        () => ({
            params: {
                id: entityId,
                noteEntity,
            },
            queryParams: {
                limit: 5,
                page: page,
                order: {
                    noteDate: 'desc',
                    id: 'desc',
                },
            },
        }),
        [entityId, noteEntity, page]
    )

    const { data: currentAgentId } = auth.CurrentAgent.useQuery({ select: (data) => data.id })
    const { isLoading: fetchingNotes, data: notesData } = notes.GetNotes.useQuery(notesParams)
    const { mutate: deleteNote } = notes.DeleteNote.useMutation()

    const notesItems = notesData?.items ?? []
    const notesPagination = notesData?.pagination ?? { totalPages: 1 }

    // effect to reset page to 1 when entityId or noteEntity changes (e.g. switching between contacts or companies)
    useEffect(() => {
        dispatch(setAccordionPage('notes', 1))
    }, [entityId, noteEntity, dispatch])

    const handleNotesModal = (noteId) => {
        const note = noteId ? notesItems.find((item) => item.id === noteId) : null

        const payload = {
            modalType: NOTES_MODAL,
            modalProps: {
                noteEntity,
                entityId,
                note: {
                    ...note,
                    agent: { id: note?.agent.id ?? currentAgentId },
                },
            },
        }

        dispatch(showModal(payload))
    }

    const handleDelete = (noteId) => {
        const callbackId = 'delete-note'
        const payload = {
            modalType: DELETE_MODAL,
            modalProps: { callbackId },
        }

        setDeleteCallback(callbackId, () =>
            deleteNote(
                { noteId, noteEntity },
                {
                    onSuccess: () => {
                        const isLastItemOnPage = notesItems.length === 1 && page > 1
                        if (isLastItemOnPage) {
                            dispatch(setAccordionPage('notes', page - 1))
                        }
                    },
                }
            )
        )

        dispatch(showModal(payload))
    }

    const handlePrevious = () => {
        dispatch(setAccordionPage('notes', page - 1))
    }

    const handleNext = () => {
        dispatch(setAccordionPage('notes', page + 1))
    }

    return (
        <>
            <UncontrolledAccordion defaultOpen={['1']} stayOpen>
                <AccordionItem className="mb-4 b-0">
                    <div className="position-relative">
                        <AccordionHeader targetId="1">
                            <h5 className="card-title my-2">{t('app.common.notes')}</h5>
                        </AccordionHeader>
                        {/* content inside div can't be inside AccordionHeader - warning appears: <button> cannot appear as a descendant of <button> */}
                        <div className="position-absolute d-flex align-items-center" style={{ top: '10px', left: '90px', zIndex: 15 }}>
                            <Button onClick={handleNotesModal} className="btn btn-label btn-add">
                                <i className="mdi mdi-plus label-icon align-middle fs-16 me-2"></i>
                                {t('app.common.addNote')}
                            </Button>
                            {notesPagination?.totalPages > 1 && (
                                <Badge color="info" className="text-white ms-2 p-2 fs-12" style={{ cursor: 'auto' }}>
                                    {`${page} / ${notesPagination.totalPages}`}
                                </Badge>
                            )}
                        </div>
                    </div>
                    <AccordionBody accordionId="1">
                        {fetchingNotes ? (
                            <SkeletonAccordion count={5} />
                        ) : (
                            <div className="acitivity-timeline">
                                {notesItems?.map((note, key) => (
                                    <div className="acitivity-item py-3 d-flex" key={key}>
                                        <div className="flex-shrink-0 avatar-xs acitivity-avatar">
                                            <div className="avatar-title bg-primary text-white rounded-circle"> B </div>
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <div className="d-flex align-items-center justify-content-between mb-4">
                                                <div>
                                                    {note.noteTitle && <h6 className="mb-1">{note.noteTitle}</h6>}
                                                    {note.noteDate && <small className="mb-0 text-muted">{formatDateTime(note.noteDate)}</small>}
                                                </div>
                                                <ul className="list-inline hstack justify-content-center">
                                                    <li>
                                                        <Button
                                                            title={t('button.edit')}
                                                            color="link"
                                                            className="text-edit"
                                                            onClick={() => handleNotesModal(note.id)}
                                                        >
                                                            <i className="mdi mdi-pencil-outline fs-16"></i>
                                                        </Button>
                                                    </li>
                                                    <li className="list-inline-item">
                                                        <Button
                                                            title={t('button.delete')}
                                                            color="link"
                                                            className="text-danger"
                                                            onClick={() => handleDelete(note.id)}
                                                        >
                                                            <i className="mdi mdi-trash-can-outline fs-16"></i>
                                                        </Button>
                                                    </li>
                                                </ul>
                                            </div>
                                            {note.note && <p className="text-secondary mb-2">{note.note}</p>}
                                        </div>
                                    </div>
                                ))}

                                {!notesItems?.length && !fetchingNotes && (
                                    <div className="d-flex justify-content-center">{t('accordion.notes.message.info')}</div>
                                )}

                                <div className="d-flex justify-content-end p-2">
                                    {page > 1 && (
                                        <div>
                                            <Button onClick={handlePrevious} color="secondary" className="btn btn-label me-3">
                                                <i className="mdi mdi-chevron-left label-icon me-2" />
                                                {t('button.navigationAction.previous')}
                                            </Button>
                                        </div>
                                    )}
                                    {page < notesPagination.totalPages && (
                                        <div>
                                            <Button onClick={handleNext} color="secondary" className="btn-label right">
                                                <i className="mdi mdi-chevron-right label-icon" />
                                                {t('button.navigationAction.next')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </AccordionBody>
                </AccordionItem>
            </UncontrolledAccordion>
        </>
    )
}

export default NotesViewAccordion
